<template>
  <UIModalContainer
    :modal-show="modalShow"
    footer-classes="flex justify-end"
    @handleClick="handleClick"
  >
    <template v-slot:header>
      <span v-if="routineData.id" v-i18n="rightBar">EDIT_TIME_TABLE_MGR</span>
      <span v-else v-i18n="rightBar">ADD_TIME_TABLE_MDR</span>
    </template>
    <template v-slot>
      <div>
        <ValidationObserver @submit="addNewRoutine">
          <InputFieldWrapper>
            <UiSingleSelect
              id="TimeTableSubjectID"
              v-model="routineData.subject_id"
              title="SUBJECT"
              :search-through-api="true"
              :options="routineInfo.subjects"
              label="title"
              rules="required"
              placeholder="Select Subject"
              reduce="id"
              @search="handleSubjectSearch"
              @change="handleSubjectChange"
            />
            <UiSingleSelect
              id="TimeTableTeacherId"
              v-model="routineData.teacher_id"
              :disabled="disabled"
              title="Teacher"
              :options="routineInfo.teachers"
              label="full_name"
              placeholder="SELECT_TEACHER"
              reduce="id"
              :image="true"
              :search-through-api="true"
              @search="handleTeacherSearch"
            />
            <UiSingleSelect
              id="TimeTableRoomId"
              v-model="routineData.room_id"
              title="Room"
              placeholder="SELECT_ROOM"
              :options="routineInfo.rooms"
              label="title"
              reduce="id"
            />
            <UiSingleSelect
              id="TimeTableDayId"
              v-model="routineData.day"
              title="DAY"
              :options="routineInfo.days"
              placeholder="SELECT_DAY"
              label="DAY"
              rules="required"
            />
            <UiTimePicker
              v-model="routineData.start_time"
              time-picker-id="TimeTableStartTimeId"
              :title="$t(`title.START_TIME`)"
              type="time"
              rules="required"
            />
            <UiTimePicker
              v-model="routineData.end_time"
              time-picker-id="TimeTableEndTimeId"
              :title="$t(`title.END_TIME`)"
              type="time"
              rules="required"
            />
          </InputFieldWrapper>

          <button ref="submit" class="hidden">Submit</button>
        </ValidationObserver>
      </div>
    </template>
    <template v-slot:footer>
      <div class="flex gap-3 rtl:gap-x-4">
        <UIButton :disabled="loadingAddNewRoutine" @click="handleClick('close')">Cancel</UIButton>
        <UIButton button="primary" @click="$refs.submit.click()">
          <template v-if="loadingAddNewRoutine">
            <Loader></Loader>
          </template>
          <span v-else-if="routineData.id">Update</span>
          <span v-else>Save</span>
        </UIButton>
      </div>
    </template>
  </UIModalContainer>
</template>

<script>
import moment from 'moment'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import UiTimePicker from '@/src/components/UiElements/UiTimePicker.vue'
import UIModalContainer from '@components/UiElements/UiModalContainer.vue'
import { mapActions, mapState } from 'vuex'
import { fullName } from '@src/utils/settings/tenant-user.util.js'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import { Form as ValidationObserver } from 'vee-validate'
import { objectDeepCopy } from '@utils/generalUtil'
import UIButton from '@src/components/UiElements/UIButton.vue'
import InputFieldWrapper from '@/src/components/UiElements/InputFieldWrapper.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { weekDays } from '@src/constants/general-constants.js'

export default {
  name: 'AddEditRoutineModal',
  components: {
    ValidationObserver,
    Loader,
    UiSingleSelect,
    UiTimePicker,
    UIModalContainer,
    UIButton,
    InputFieldWrapper,
  },
  props: {
    modalShow: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: null,
    },
    getSubjectsRoomsForClass: {
      type: Function,
      required: true,
    },
    getTeachersForSubject: {
      type: Function,
      required: true,
    },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      disabled: false,
      loadingAddNewRoutine: false,
      rightBar: 'rightBar',
      routineInfo: {
        subjects: [],
        teachers: [],
        rooms: [],
        days: weekDays,
      },
      routineData: {
        day: '',
        section_id: null,
        subject_id: null,
        teacher_id: null,
        room_id: null,
        color: null,
        start_time: null,
        end_time: null,
      },
    }
  },
  computed: {
    ...mapState({
      activeRole: (state) => state.layout.activeRole,
      currentUser: (state) => state.auth.currentUser,
      currentSectionId: (state) => state.layout.currentSectionScope?.id,
    }),
  },
  watch: {
    modalShow: {
      immediate: true,
      handler(status) {
        if (status) {
          this.initCreateRoutine()
        }
      },
    },
    editData: {
      immediate: true,
      handler(data) {
        if (data && this.modalShow) {
          this.loadEditData(data)
        }
      },
    },
  },
  methods: {
    handleClick(eventType) {
      if (eventType === 'close') {
        this.closeModal()
      }
    },

    closeModal() {
      this.$store.dispatch('layout/setShowModal', false)
      this.clearNewRoutineData()
      this.$emit('close')
    },

    clearNewRoutineData() {
      this.routineData.day = ''
      this.routineData.section_id = this.currentSectionId
      this.routineData.subject_id = null
      this.routineData.teacher_id = null
      this.routineData.room_id = null
      this.routineData.color = null
      this.routineData.start_time = null
      this.routineData.end_time = null

      if (this.routineData.id) {
        delete this.routineData.id
      }
    },

    loadEditData(event) {
      const subjects = this.getSubjectsRoomsForClass('', event.section_id)
      const teachers = this.getTeachersForSubject('')
      Promise.all([subjects, teachers]).then((values) => {
        this.routineInfo.subjects = values[0].subjects
        this.routineInfo.rooms = values[0].rooms
        this.routineInfo.teachers = values[1].teachers
        const subjectsList = this.routineInfo.subjects || []
        if (
          event.subject &&
          !subjectsList.some((s) => s && s.id === (event.subject.id ?? event.subject.subjectId))
        ) {
          const subjectToAdd = { ...event.subject, id: event.subject.id ?? event.subject.subjectId }
          this.routineInfo.subjects.push(subjectToAdd)
        }
        const teachersList = this.routineInfo.teachers || []
        if (event.teacher_obj && !teachersList.some((t) => t && t.id === event.teacher_id)) {
          const teacherToAdd = { ...event.teacher_obj }
          if (!teacherToAdd.full_name) fullName(teacherToAdd)
          this.routineInfo.teachers.push(teacherToAdd)
        }

        this.routineData.id = event.id
        this.routineData.section_id = event.section_id
        this.routineData.subject_id = event.subject.subjectId
        this.routineData.room_id = event.room_id
        this.routineData.teacher_id = event.teacher_id
        this.routineData.day = event.day.charAt(0).toUpperCase() + event.day.slice(1)
        this.routineData.color = event.color
        this.routineData.start_time = event.startTime.slice(0, -3)
        this.routineData.end_time = event.endTime.slice(0, -3)
      })
    },

    initCreateRoutine() {
      this.routineData.section_id = this.currentSectionId
      this.getSubjectsRoomsForClass('', this.currentSectionId).then((data) => {
        this.routineInfo.subjects = data.subjects
        this.routineInfo.rooms = data.rooms

        if (this.activeRole === TENANT_ROLES.SECTION_TEACHER) {
          this.disabled = true
          const user = objectDeepCopy(this.currentUser)
          this.routineInfo.teachers.push(user)
          fullName(user)
          this.routineData.teacher_id = user?.id
        }
      })
    },

    handleSubjectSearch(searchQuery) {
      this.getSubjectsRoomsForClass(searchQuery, this.currentSectionId).then((data) => {
        this.routineInfo.subjects = data.subjects
        this.routineInfo.rooms = data.rooms
      })
    },

    handleSubjectChange() {
      this.routineData.teacher_id = null
      this.getTeachersForSubject('').then((data) => {
        this.routineInfo.teachers = data.teachers
      })
    },

    handleTeacherSearch(searchQuery) {
      this.getTeachersForSubject(searchQuery).then((data) => {
        this.routineInfo.teachers = data.teachers
      })
    },

    addNewRoutine() {
      if (!this.loadingAddNewRoutine) {
        this.loadingAddNewRoutine = true
        const routineDate = moment()

        const routine = {
          day: this.routineData.day.toLowerCase(),
          started_at_time: this.routineData.start_time,
          ended_at_time: this.routineData.end_time,
          room_id: this.routineData.room_id,
          subject_id: this.routineData.subject_id,
          section_id: this.currentSectionId,
          is_active: true,
          date: routineDate.format('YYYY-MM-DD'),
          to_date: routineDate.add(5, 'days').format('YYYY-MM-DD'),
        }

        if (this.routineData.teacher_id !== null && this.routineData.teacher_id !== undefined) {
          routine.teacher_id = this.routineData.teacher_id
        }

        const data = {
          routine,
          id: this.routineData.id,
        }

        this.addRoutine(data)
          .then(() => {
            this.closeModal()
            this.$emit('saved')
          })
          .finally(() => {
            this.loadingAddNewRoutine = false
          })
      }
    },
    ...mapActions('routine', ['addRoutine']),
  },
}
</script>
